import React, { CSSProperties } from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, Decoder, HTMLParser, Image, KeepProportions, SvgIcon } from '@gik/ui';
import moment, { Moment } from 'moment';
import { ICalendarEntry, ICalendarEvent } from '@gik/calendar/models/Calendar';
import { isInPastAllTimezones } from '@gik/core/utils/DateTimeUtils';
import { useUserStore } from '@gik/core/store/UserStore';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { CalendarUtil } from '@gik/calendar';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useInkind } from '@gik/api/inkinds/inkind';
import { isAnnouncement } from '../EventForm/utils';
import { IWordpressCalendarEventType } from '@gik/core/models/wordpress/WordpressCalendarEventType';
import { useCalendarEventTypes } from '@gik/api/calendar/calendarEventType';
import { CalendarEventTypeIds } from '@gik/calendar/models/CalendarEventTypes';
import { isGiftCardServiceModule } from '@gik/calendar/constants/services/ServiceModules';
import { ICalendarClaimCartItem } from '@gik/calendar/models/Claim';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';
import { translationKeys as calTranslationKeys } from '@gik/calendar/i18n/en';
import DateTimeDisplay from '../DateTimeDisplay/DateTimeDisplay';
import { openUserInfoModal } from '@gik/user-profile/components/UserInfoModal/UserInfoModal';
import { BaseUserWithAvatar } from '@gik/core/models/gik/User';

export interface ICalendarEventProps extends React.HTMLAttributes<HTMLDivElement> {
  event: ICalendarEvent;
  entry: ICalendarEntry;
  eventTypes?: IWordpressCalendarEventType[];
  selectedMonth?: Moment;
  resolvingClaim?: boolean;
  onEdit?(event: ICalendarEvent, entry: ICalendarEntry): void;
  onClaim?(event: ICalendarEvent, entry: ICalendarEntry): void;
}

export function CalendarEvent({
  event,
  entry,
  eventTypes,
  className,
  resolvingClaim,
  onEdit,
  onClaim,
  ...otherProps
}: ICalendarEventProps): React.ReactElement {
  const bem = bemBlock('calendar-event');
  const { t } = useTranslation();

  const { data: calendarEventTypes } = useCalendarEventTypes({
    initialData: eventTypes,
  });
  const userId = useUserStore(state => state.id);

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  const isClaimed = !!entry.claim;
  const canEdit = canUserEditPage(inkind, userId);
  const isPastEvent = isInPastAllTimezones(moment.utc(event.startsAt));
  const isGiftCardClaim = entry.claim && isGiftCardServiceModule(entry.claim.serviceModule);
  const uneditable = isGiftCardClaim || (isClaimed && isPastEvent);

  const eventType = calendarEventTypes?.find(type => type.id === entry.typeId);

  let giftCardThumbnail: string;
  const placeholderThumbnail = undefined; // TODO: set placeholder image here

  if (isGiftCardClaim && entry.claim?.cartItems?.length) {
    const cartItem: ICalendarClaimCartItem = entry.claim?.cartItems[0];
    giftCardThumbnail = cartItem.thumbnailUrl || cartItem.imageUrl;
    if (!giftCardThumbnail) {
      giftCardThumbnail = placeholderThumbnail;
    }
  }

  function handleClaim(entry: ICalendarEntry, event: ICalendarEvent) {
    onClaim(event, entry);
  }

  function handleClick(ev: React.MouseEvent<HTMLDivElement>) {
    ev.stopPropagation();

    if (isClaimed) {
      CalendarUtil.viewClaim(entry, event);
    } else {
      if (isPastEvent) {
        if (!uneditable) {
          onEdit(event, entry);
        }
      } else {
        // when anonymouse users click the event it will attempt to claim the event
        onClaim(event, entry);
      }
    }
  }

  if (!eventType) {
    console.error(`event type with id "${entry.typeId}" not found`);
    return null;
  }

  if (!eventTypes || !inkind) {
    return null;
  }

  const _isAnnouncement = isAnnouncement(eventType.id);

  const clickable = true;

  let claimerFullname: string = null;
  if (entry.claim) {
    claimerFullname = entry.claim.supporter.firstName;
    if (entry.claim.supporter.lastName) {
      claimerFullname += ' ' + entry.claim.supporter.lastName;
    }
  }

  function openSupporterInfoModal(e: React.MouseEvent, supporter: BaseUserWithAvatar) {
    e.stopPropagation();
    openUserInfoModal(supporter);
  }

  return (
    <div
      className={bem(
        null,
        [
          { organizer: canEdit },
          { clickable },
          { uneditable },
          { 'past-event': isPastEvent },
          { announcement: _isAnnouncement },
        ],
        [className, `gik-calendar-event-type-theme--${eventType?.slug}`]
      )}
      {...otherProps}
      onClick={handleClick}
    >
      {_isAnnouncement ? (
        <>
          <header className={bem('content-wrapper')}>
            {/* TODO: show icon based on announcementType */}
            <SvgIcon name="HeartOutline" size="xl" />
            {/* <SvgIcon name="InformationCircleOutline" /> */}
            {/* <SvgIcon name="ExclamationOutline" /> */}
          </header>
          <main className={bem('content-main')}>
            <span className={bem('title', null)}>{eventType?.name}</span>
            <p className={bem('announcement-description')}>
              <HTMLParser rawHtml={entry?.description} />
            </p>
          </main>
        </>
      ) : (
        <>
          <div className={bem('content-wrapper')}>
            <header className={bem('content-header')}>
              {!isGiftCardClaim && (
                <DateTimeDisplay
                  timeSeparator=" to "
                  hideDate
                  startsAt={event.startsAt}
                  endsAt={event.endsAt}
                  allDay={entry.allDay && !isGiftCardClaim}
                />
              )}
              {isGiftCardClaim && <div className={bem('box-giftcard')}>{t(translationKeys.giftCard)}</div>}
            </header>
            <main className={bem('content-main')}>
              <div className={bem('content-title')}>
                {entry.typeId === CalendarEventTypeIds.Other ? (
                  <Image
                    inline
                    className={bem('icon', null)}
                    src={eventType?.acf.simplified_svg_icon_alt || eventType?.acf.simplified_svg_icon}
                  />
                ) : (
                  <Image inline className={bem('icon', null)} src={eventType?.acf.simplified_svg_icon} />
                )}
                <div className={bem('label')}>
                  <span className={bem('title')}>
                    {eventType.id === CalendarEventTypeIds.Other
                      ? <HTMLParser rawHtml={entry?.title} /> || 'No Title'
                      : eventType?.name}
                  </span>
                  {/* Todo get correct number of people from event data */}
                  {entry.numberRequired && <span className={bem('number-of-people')}> for {entry.numberRequired}</span>}
                  {/* {entry.typeId === CalendarEventTypeIds.Other && (
                    <div className={bem('custom-title')}>{entry.title}</div>
                  )} */}
                </div>
              </div>

              <div className={bem('date-mobile', null, 'gik-hidden-md')}>
                <DateTimeDisplay
                  timeSeparator=" to "
                  hideDate
                  startsAt={event.startsAt}
                  endsAt={event.endsAt}
                  allDay={entry.allDay && !isGiftCardClaim}
                />
              </div>

              <div className={bem('content')}>
                {isGiftCardClaim && (
                  <>
                    <div className={bem('giftcard', null)}>
                      <div
                        className={bem('thumbnail')}
                        style={{ backgroundImage: `url(${giftCardThumbnail})` } as CSSProperties}
                      />
                      <KeepProportions proportion={0.63}>
                        <Image className={bem('thumbnail')} src={giftCardThumbnail} />
                      </KeepProportions>
                    </div>
                    {/* <div className={bem('box-time', null, 'print tw-uppercase')}>
                      <span>Gift Card&nbsp;</span>
                    </div> */}
                  </>
                )}

                {isClaimed && (
                  <div className={bem('providedBy')}>
                    {entry.typeId !== CalendarEventTypeIds.PetCare &&
                      entry.typeId !== CalendarEventTypeIds.ChildCare &&
                      entry.typeId !== CalendarEventTypeIds.SeniorCare &&
                      entry.typeId !== CalendarEventTypeIds.HomeServices &&
                      entry.typeId !== CalendarEventTypeIds.Transportation && (
                        <div className={bem('providedBy-description')}>
                          <HTMLParser rawHtml={entry.claim?.description} />
                        </div>
                      )}
                    {isGiftCardClaim && (
                      <div className={bem('providedBy-giftcard')}>{t(translationKeys.giftCard)}&nbsp;</div>
                    )}
                    <div className={bem('providedBy-from')}>
                      <span>{t(calTranslationKeys.from)}</span>{' '}
                      <Button
                        className={bem('name-link')}
                        variant={'default-link'}
                        onClick={e => openSupporterInfoModal(e, entry.claim.supporter)}
                      >
                        <Decoder text={claimerFullname} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </main>
          </div>
          <div className={bem('actions')}>
            {isClaimed && (
              <div className={bem('claimed')}>
                <SvgIcon name="CheckOutline" />
              </div>
            )}

            {!isClaimed && !isPastEvent && (
              <div className={bem('claim-actions', null, ['no-print'])}>
                <Button
                  pill
                  className="upper"
                  onClick={ev => {
                    ev.stopPropagation();
                    handleClaim(entry, event);
                  }}
                >
                  {t(
                    resolvingClaim
                      ? translationKeys.calendarEventConflictResolutionClaimButton
                      : translationKeys.calendarEventClaimButton
                  )}
                </Button>
              </div>
            )}

            {/* {event.userId && userId && event.userId.toLowerCase() !== userId.toLowerCase() && (
          <div className="claimed-check no-print">
            <SvgIcon name="CheckOutline" />
          </div>
        )} */}
          </div>
        </>
      )}
    </div>
  );
}
